(() => {
  if (window.ProhealthCartUnlockedGifts) return;

  const MODULE = '[data-ph-unlock-gifts]';
  const CARD = '[data-ph-gift-card]';
  const CAROUSEL = '[data-ph-gift-carousel]';
  const GIFT_PROPERTY = '_ph_unlock_gift';
  const MAX_TIERS = 6;

  let reconcileTimer = null;
  let checkoutBypass = false;
  let operationQueue = Promise.resolve();

  const rootUrl = () => window.Shopify?.routes?.root || '/';

  /* ---------------------------------------------------------------------
     Write lock

     The theme changes quantities with cart/change.js, which addresses a
     line by its POSITION in the cart, read from the HTML that is currently
     on screen. This file adds and removes the gift line, and Shopify
     reorders lines when one is added or its quantity changes. So while one
     of our writes is in flight, every data-index in the rendered cart is
     potentially wrong, and a click on minus can be sent for a line that is
     no longer where the page thinks it is.

     Two measures. This lock makes the quantity and remove controls
     unclickable for the fraction of a second our own write takes, and
     THEME_COOLDOWN below keeps us from writing straight after the theme
     has written, so the two never overlap.
     --------------------------------------------------------------------- */

  const BUSY_CLASS = 'ph-cart-busy';
  let busyCount = 0;

  const setBusy = (on) => {
    busyCount = Math.max(0, busyCount + (on ? 1 : -1));
    document.documentElement.classList.toggle(BUSY_CLASS, busyCount > 0);
  };

  const withBusy = async (run) => {
    setBusy(true);
    try {
      return await run();
    } finally {
      setBusy(false);
    }
  };

  /* How long to stay out of the way after the theme's own cart write.

     Long enough that the theme's re-render, which it starts at the same
     moment, has landed before we paint on top of it. Not longer: this used
     to be 700ms, and since the cooldown restarts on the very cart event
     that wakes this file, every automatic selection paid it in full. That
     was most of the delay between crossing a threshold and seeing the gift.
     The hazard it originally guarded against, our writes racing the
     theme's position-addressed ones, was removed at the root when every
     write became key-addressed. */
  const THEME_COOLDOWN = 250;
  let lastThemeMutation = 0;

  const themeIsBusy = () => Date.now() - lastThemeMutation < THEME_COOLDOWN;

  /* Waiting out the theme is a courtesy, never a reason to stop responding.
     Every write goes through the operation queue, so one wait that never
     ends would freeze the queue and every later click with it, silently.
     Capped: after this the write goes ahead regardless. */
  const MAX_THEME_WAIT = 2000;

  const waitForTheme = async () => {
    const deadline = Date.now() + MAX_THEME_WAIT;

    while (themeIsBusy() && Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        const remaining = THEME_COOLDOWN - (Date.now() - lastThemeMutation) + 40;
        window.setTimeout(resolve, Math.max(Math.min(remaining, deadline - Date.now()), 20));
      });
    }
  };

  const queueOperation = (operation) => {
    const queued = operationQueue.then(operation, operation);
    operationQueue = queued.catch(() => undefined);
    return queued;
  };

  const getCart = async () => {
    const response = await fetch(`${rootUrl()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Unable to retrieve the latest cart.');
    return response.json();
  };

  const giftItems = (cart) =>
    (cart?.items || []).filter((item) => item.properties?.[GIFT_PROPERTY] === 'true');

  // cart.total_price includes line-level and cart-level discounts. Subtracting
  // marked gift-line value leaves the discounted paid-merchandise subtotal.
  const paidSubtotal = (cart) => {
    const giftLineValue = giftItems(cart).reduce(
      (total, item) => total + Number(item.final_line_price || 0),
      0
    );
    return Math.max(Number(cart?.total_price || 0) - giftLineValue, 0);
  };

  const giftQuantity = (cart) =>
    giftItems(cart).reduce((total, item) => total + Number(item.quantity || 0), 0);

  /* ---------------------------------------------------------------------
     Header count

     The gift line is hidden from the cart product list, so it must not be
     counted in the header bubble either: a customer with two products in
     the drawer and a 3 on the icon would reasonably think something is
     missing.

     Two halves, because the number is written from two places. Liquid
     renders it on first paint (snippets/cart.liquid, gift subtracted
     there). After that the theme rewrites it from cart.json, in
     MCartTemplate.updateCartCount and MCartDrawer.updateCartCount, so both
     prototypes are wrapped to subtract the same amount.

     The wrap is applied lazily: cart.js and cart-drawer.js are body
     scripts and neither custom element is defined when this file runs.
     --------------------------------------------------------------------- */

  let hiddenGiftQuantity = 0;
  const patchedCounters = new WeakSet();

  const patchCartCount = () => {
    ['m-cart', 'm-cart-drawer'].forEach((tag) => {
      const proto = window.customElements?.get?.(tag)?.prototype;
      /* Own property only. m-cart-drawer-items inherits this method from
         m-cart, and wrapping the inherited copy on the subclass would
         subtract the gift twice. */
      if (!proto || !Object.prototype.hasOwnProperty.call(proto, 'updateCartCount')) return;
      if (patchedCounters.has(proto)) return;

      patchedCounters.add(proto);
      const original = proto.updateCartCount;
      proto.updateCartCount = function updateVisibleCartCount(count) {
        return original.call(this, Math.max(Number(count || 0) - hiddenGiftQuantity, 0));
      };
    });
  };

  /* ---------------------------------------------------------------------
     The missing cart event on add to cart

     This is the reason the gift was not added at the moment the customer
     crossed a threshold, which is very nearly always by adding a product.

     In theme-global.js the add-to-cart branch, when the drawer is enabled,
     does three things: MCartDrawer.renderContents(response), a success
     notification, and an emit of **openCartDrawer**. It never emits
     cartUpdate. So the drawer repaints with the new total, the reward strip
     correctly announces that a gift has been unlocked, and every module
     that listens for cartUpdate is never told anything happened. Ours is
     one of them, so the reconcile that adds the gift did not run until
     something else wrote to the cart, or the page was reloaded. That is
     exactly the reported symptom: a strip that says "you have unlocked a
     free gift" above a card that still says "Choose this".

     The delivery module escaped it by accident: its MutationObserver
     watches for its own module being replaced and re-checks the cart, which
     is why the progress module was right while the gift block was stale.

     Fixed at source, by wrapping renderContents so it announces itself.
     Every module that listens for cartUpdate then behaves the same on add
     to cart as it does on a quantity change, this file included.

     The event is emitted with source 'main-cart-items' deliberately. That
     is the theme's own marker for "the cart DOM has already been repainted
     by whoever emitted this", and it is true here: renderContents has just
     rebuilt the drawer. MCartTemplate.connectedCallback skips that source,
     so the wrap does not cause a pointless second section fetch, while the
     count bubble subscription and every other listener still run.
     --------------------------------------------------------------------- */

  const patchedRenderers = new WeakSet();

  const patchDrawerRender = () => {
    const proto = window.customElements?.get?.('m-cart-drawer')?.prototype;
    if (!proto || !Object.prototype.hasOwnProperty.call(proto, 'renderContents')) return;
    if (patchedRenderers.has(proto)) return;

    patchedRenderers.add(proto);
    const original = proto.renderContents;

    proto.renderContents = function renderContentsAndAnnounce(...args) {
      const result = original.apply(this, args);

      if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
          source: 'main-cart-items'
        });
      }

      return result;
    };
  };

  const applyCartCount = (cart) => {
    if (!cart) return;
    hiddenGiftQuantity = giftQuantity(cart);
    const visible = Math.max(Number(cart.item_count || 0) - hiddenGiftQuantity, 0);

    document.querySelectorAll('.m-cart-count-bubble').forEach((bubble) => {
      bubble.textContent = visible;
      /* The two surfaces use different hiding classes. Set both so the
         bubble behaves the same whichever one the CSS acts on. */
      bubble.classList.toggle('m:hidden', visible === 0);
      bubble.classList.toggle('hidden', visible === 0);
    });
  };

  const getReferenceModule = (context = document) =>
    context?.querySelector?.(MODULE) || document.querySelector(MODULE);

  const parseVariantIds = (value) =>
    new Set(
      String(value || '')
        .split('|')
        .map((variantId) => Number(variantId))
        .filter((variantId) => Number.isFinite(variantId) && variantId > 0)
    );

  const getTierConfig = (module) => {
    if (!module) return [];

    const tiers = [];
    let previousThreshold = 0;

    for (let index = 1; index <= MAX_TIERS; index += 1) {
      const threshold = Number(module.getAttribute(`data-ph-tier-${index}-threshold`) || 0);
      const variantIds = parseVariantIds(module.getAttribute(`data-ph-tier-${index}-variants`));

      if (threshold > previousThreshold && variantIds.size > 0) {
        tiers.push({ index, threshold, variantIds });
        previousThreshold = threshold;
      }
    }

    return tiers;
  };

  const getUnlockedTier = (tiers, subtotal) => {
    let unlocked = null;
    tiers.forEach((tier) => {
      if (subtotal >= tier.threshold) unlocked = tier;
    });
    return unlocked;
  };

  const includesPreviousTiers = (module) =>
    module?.dataset?.includePreviousTiers === 'true';

  const isTierAllowed = (tier, unlockedTier, includePrevious) => {
    if (!tier || !unlockedTier) return false;
    return includePrevious ? tier.index <= unlockedTier.index : tier.index === unlockedTier.index;
  };

  const getItemTier = (item, tiers) => {
    const itemTierIndex = Number(item?.properties?._ph_unlock_tier || 0);
    const itemThreshold = Number(item?.properties?._ph_unlock_threshold || 0);
    return tiers.find(
      (tier) => tier.index === itemTierIndex && tier.threshold === itemThreshold
    ) || null;
  };

  const giftMatchesAllowedTier = (item, tiers, unlockedTier, includePrevious) => {
    const itemTier = getItemTier(item, tiers);
    if (!item || !itemTier || !isTierAllowed(itemTier, unlockedTier, includePrevious)) return false;

    return (
      Number(item.quantity) === 1 &&
      Number(item.final_line_price || 0) === 0 &&
      itemTier.variantIds.has(Number(item.variant_id))
    );
  };

  /* cart/update.js addresses lines by KEY, not by position, so our own
     writes are immune to reordering. The lock is here for the theme's
     benefit, not ours. */
  const updateCartLines = async (updates) => {
    if (!Object.keys(updates).length) return getCart();

    await waitForTheme();

    return withBusy(async () => {
      const response = await fetch(`${rootUrl()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) throw new Error('Unable to update the gift in the cart.');
      return response.json();
    });
  };

  /* Removing a line is the only destructive thing this file does, so it is
     the one place that gets checked twice.

     Two guards:
       1. Re-read the cart. The list passed in may be seconds old.
       2. Only ever touch a line that still carries the gift property, taken
          from that fresh read. A key that no longer matches a gift line is
          dropped, never zeroed. This is what makes the whole operation
          safe: nothing a customer paid for can be in `updates`.

     A THIRD GUARD USED TO SIT HERE AND WAS WRONG. It refused any update
     that would leave the cart with no lines, on the reasoning that a basket
     of gifts only is not a state this code can create. That stopped being
     true in v14, when the gift line was hidden from the product list.

     Remove the last paid product from a cart that holds a gift and the cart
     is momentarily gift-only, which is precisely when the gift must go. The
     guard refused, the gift stayed, and because it is hidden the customer
     saw an empty product list, a cart that would not empty, and an orange
     notice telling them their rewards had been removed. Reported as "I
     cannot remove products, an error appears" — and it only ever happened
     on the last one, which is why it looked intermittent.

     A cart containing nothing but free gifts should end up empty. Guard 2
     already guarantees only gift lines can be listed here, so there is
     nothing further to protect against. */
  const removeGiftItems = async (items, exceptKey = '') => {
    const fresh = await getCart();
    const freshGiftKeys = new Set(giftItems(fresh).map((item) => item.key));

    const updates = {};
    items.forEach((item) => {
      if (item.key !== exceptKey && freshGiftKeys.has(item.key)) updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) return fresh;

    return updateCartLines(updates);
  };

  /* One re-render per surface, and every surface actually repainted.

     The two cart surfaces refresh by different routes, and this is the
     single fact the previous version got wrong.

       Cart page   m-cart subscribes to cartUpdate in connectedCallback and
                   calls onCartUpdate for any event that is not its own, so
                   emitting is enough. Calling onCartUpdate as well would
                   fetch and repaint twice.
       Drawer      nothing subscribes m-cart-drawer to anything. cart.js
                   gives m-cart-drawer-items the inherited onCartUpdate,
                   which early-returns on everything because isCartPage is
                   false. So an emit alone repaints nothing in the drawer.
                   onCartDrawerUpdate has to be called by hand.

     Emitting alone left the drawer showing the cart as it stood before our
     write. That is why a gift stayed on screen after the total dropped
     below the tier, why the added gift still read "Choose this", and why a
     tap on a card looked like it did nothing: the write succeeded every
     time, the drawer just never redrew. */
  const refreshThemeCart = () => {
    const drawer = document.querySelector('m-cart-drawer');
    if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
      drawer.onCartDrawerUpdate(true);
    }

    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
        source: 'prohealth-unlocked-gift'
      });
      return;
    }

    /* No event bus: the cart page has to be called directly too. */
    const page = document.querySelector('m-cart');
    if (page && typeof page.onCartUpdate === 'function') {
      page.onCartUpdate(true);
    }
  };

  /* One notice region per cart surface, at the top of the drawer, rather
     than a toast rendered inside the scrolling body where it could appear
     off screen. Never window.alert: a modal dialog blocks the drawer and
     the customer cannot act on it. */
  const noticeTarget = (module) => {
    const surface = module?.closest?.('m-cart-drawer, m-cart, .m-cart-drawer, .m-cart') || document;
    return (
      surface.querySelector?.('[data-ph-cart-notice-host]') ||
      document.querySelector('[data-ph-cart-notice-host]') ||
      module ||
      document.body
    );
  };

  /* ---------------------------------------------------------------------
     Cart notices are rendered and removed by this file, not by the theme

     The theme's Notification is a singleton that inserts itself with
     method: 'after', so it lands as a SIBLING of whatever it is handed
     rather than inside it. Ours therefore ended up loose in the drawer
     header, which is outside every region the cart re-renders, and its
     lifetime belonged to a component shared with the rest of the theme.
     The result was a notice about a gift that had long since been sorted
     out still sitting on screen while the customer carried on shopping.

     Rendering it here costs a few lines and buys three things the bug
     needed: it goes INSIDE the notice host, it has a lifetime this file
     controls, and it can be taken down the moment it stops being true.

     The markup is a copy of the theme's own component, face icon included,
     so it looks no different from any other cart notice. Never
     window.alert: a modal dialog blocks the drawer.
     --------------------------------------------------------------------- */

  const NOTICE_MS = 6000;
  let noticeTimer = null;
  let noticeKey = '';

  const clearNotice = () => {
    window.clearTimeout(noticeTimer);
    noticeKey = '';
    document.querySelectorAll('[data-ph-cart-notice-host]').forEach((host) => {
      host.replaceChildren();
    });
  };

  const notify = (module, message, type = 'warning', key = '') => {
    if (!message) return;

    /* The same message for the same state is not news. Without this a
       reconcile that runs on every cart event re-posts the notice and it
       reads as one that never goes away. */
    const identity = key || `${type}:${message}`;
    if (identity === noticeKey) return;

    const target = noticeTarget(module);
    if (!target) return;

    const SMILE = 'M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
    const FROWN = 'M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';

    const notice = document.createElement('div');
    notice.className = `notification ${type} show`;
    notice.setAttribute('role', 'status');
    notice.innerHTML =
      '<svg class="m-svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" ' +
      'xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="' +
      (type === 'success' ? SMILE : FROWN) +
      '"/></svg><div class="notification__text"></div>';
    notice.querySelector('.notification__text').textContent = message;

    window.clearTimeout(noticeTimer);
    noticeKey = identity;
    target.replaceChildren(notice);

    noticeTimer = window.setTimeout(() => {
      notice.remove();
      noticeKey = '';
    }, NOTICE_MS);
  };

  const validateCart = async ({ cart: suppliedCart = null, module: suppliedModule = null } = {}) => {
    const module = suppliedModule || getReferenceModule();
    let cart = suppliedCart || await getCart();
    const tiers = getTierConfig(module);
    const includePrevious = includesPreviousTiers(module);

    let subtotal = paidSubtotal(cart);
    let unlockedTier = getUnlockedTier(tiers, subtotal);
    let gifts = giftItems(cart);
    let validGift = gifts.find((item) =>
      giftMatchesAllowedTier(item, tiers, unlockedTier, includePrevious)
    ) || null;
    let changed = false;

    /* WHY giftRemoved IS SET HERE AND NOT ONLY BELOW

       This branch takes off any gift line that is not the one the customer
       is entitled to, which includes the whole of the case that matters:
       the total drops a tier, the gift on the order stops qualifying,
       validGift is null, and every gift line is removed right here.

       The second branch further down also sets giftRemoved, and it used to
       be the only place that did. By the time it ran the cart had already
       been emptied of gifts by this one, so its `gifts.length` test was
       false and the flag was never set. The result was that the two notices
       she wrote, "your free gift has been changed" and "rewards removed",
       could not appear at all: the condition that raises them was
       unreachable. Both are settings she can see and edit, which made it
       look as though the copy was being ignored.

       So the flag is raised at the point the removal actually happens. A
       swap between two valid gifts does not raise it, because validGift is
       still there. */
    const invalidOrDuplicateGifts = gifts.filter((item) => item.key !== validGift?.key);
    let giftRemoved = false;

    if (invalidOrDuplicateGifts.length) {
      cart = await removeGiftItems(gifts, validGift?.key || '') || cart;
      changed = true;
      if (!validGift) giftRemoved = true;
    }

    // Cart-level discount allocation can change after removing a line. Re-read
    // eligibility from the updated cart response and validate the retained gift.
    subtotal = paidSubtotal(cart);
    unlockedTier = getUnlockedTier(tiers, subtotal);
    gifts = giftItems(cart);
    validGift = gifts.find((item) =>
      giftMatchesAllowedTier(item, tiers, unlockedTier, includePrevious)
    ) || null;

    /* Belt and braces: anything the branch above left behind, for instance a
       gift line added between the read and the write. */
    if (gifts.length && !validGift) {
      cart = await removeGiftItems(gifts) || cart;
      changed = true;
      giftRemoved = true;
      subtotal = paidSubtotal(cart);
      unlockedTier = getUnlockedTier(tiers, subtotal);
      gifts = giftItems(cart);
      validGift = null;
    }

    applyCartCount(cart);

    return { cart, changed, subtotal, unlockedTier, validGift, giftRemoved };
  };

  /* Which of the two notices to show after a gift has been taken off.

     No tier left  every reward is gone, so say so and name the amount that
                   brings them back.
     A tier left   the gift itself is being swapped for one from the tier
                   they are now in. Auto-selection adds it straight after,
                   so the message explains a change, not a loss. */
  const announceGiftRemoval = (module, result) => {
    if (!module) return;

    /* Nothing was taken off, so anything still on screen about a gift being
       taken off has stopped being true. This is the half that was missing:
       the notice was raised but never withdrawn, so it survived every later
       change to the cart, including the ones that put the reward back. */
    if (!result?.giftRemoved) {
      clearNotice();
      return;
    }

    const { giftChangedMessage, rewardsRemovedMessage } = module.dataset;

    /* Keyed by which message and which tier, so re-entering the same state
       can announce it again while sitting in it cannot. */
    if (!result.unlockedTier) {
      notify(module, rewardsRemovedMessage, 'warning', 'rewards-removed');
      return;
    }

    notify(module, giftChangedMessage, 'warning', `gift-changed:${result.unlockedTier.index}`);
  };

  const reconcileNow = (options = {}) =>
    queueOperation(async () => {
      const module = options.module || getReferenceModule();
      if (!module) return { cart: options.cart || null, changed: false };

      try {
        const result = await validateCart({ cart: options.cart, module });
        announceGiftRemoval(module, result);
        if (result.changed || options.refresh) refreshThemeCart();
        return result;
      } catch (error) {
        console.error('Prohealth unlocked gift validation:', error);
        if (options.notifyOnError) notify(module, error.message, 'error');
        throw error;
      }
    });

  const scheduleReconcile = () => {
    window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(async () => {
      /* Let the theme finish its own write and its re-render before
         touching the cart, so the line positions on screen are never stale
         while we are adding or removing the gift. */
      await waitForTheme();
      reconcileNow()
        .then((result) => maybeAutoSelect(getReferenceModule(), result))
        .catch(() => undefined);
    }, 120);
  };

  /* Adds one gift and validates it.

     The descriptor is either a rendered card or a plain object built from
     the tier data attributes. Automatic selection needs the second form:
     the moment a threshold is crossed, the module still on screen is the
     one rendered below it, which has no cards in it at all, so a chooser
     that could only read the DOM had nothing to choose from and the gift
     was never added. The data attributes are on the module in every state.

     { module, variantId, tier, threshold, card } */
  /* Paint the chosen card, now, with no network involved.

     This is what makes choosing a gift feel immediate. The write still has
     to happen, and the reconcile still has the last word, but the customer
     sees their choice in the same frame as the tap instead of a second and
     a half later. */
  /* The card is a div with role="button" so its title can be a real link,
     so there is no .disabled to read any more. */
  const cardUnavailable = (card) =>
    Boolean(card && (card.dataset.unavailable === 'true' || card.disabled));

  const paintSelection = (module, variantId) => {
    const chosen = Number(variantId);

    module.querySelectorAll(CARD).forEach((card) => {
      const isChosen = Number(card.dataset.variantId) === chosen;
      card.classList.toggle('is-selected', isChosen);
      card.classList.toggle('is-pending', isChosen);
      card.setAttribute('aria-pressed', isChosen ? 'true' : 'false');
    });

    /* Both headings are in the markup; this picks which one shows. */
    module.classList.toggle('ph-unlock-gifts--no-gift', !chosen);
  };

  /* Show the gift as selected on the very first paint of a new render.

     Without this the customer sees two states on every threshold crossing:
     the block renders with a tier unlocked and nothing chosen yet, reading
     "choose your free gift" with an unselected card, and a few hundred
     milliseconds later the automatic selection lands and it flips to
     "added". That flip is the flicker.

     There is no guesswork in it. If a tier is unlocked and no gift is on the
     order, the automatic selection is about to add the first available gift
     of that tier, so this paints the same card that is about to be
     confirmed. Should the add fail, the catch in applyGift repaints from the
     server and the card returns to unselected.

     Only ever paints forward, never clears an existing selection. */
  const predictSelection = (module) => {
    if (Number(module.dataset.unlockedTier || 0) < 1) return;
    if (module.dataset.selectedKey) return;
    if (module.querySelector(`${CARD}.is-selected`)) return;

    const tiers = getTierConfig(module);
    const choice = defaultGiftChoice(module, tiers, anchorTier(module));
    if (!choice) return;

    paintSelection(module, choice.variantId);
  };

  const clearPending = (module) => {
    module.querySelectorAll(`${CARD}.is-pending`).forEach((card) => {
      card.classList.remove('is-pending');
    });
  };

  const applyGift = (choice, options = {}) =>
    queueOperation(async () => {
      const card = choice.card || null;
      if (cardUnavailable(card)) return;
      const isAuto = options.auto === true;

      const module = choice.module;
      const variantId = Number(choice.variantId);
      const cardTierIndex = Number(choice.tier);
      const cardThreshold = Number(choice.threshold);
      if (!module || !variantId || !cardTierIndex || !cardThreshold) return;

      /* Tier configuration comes from the module's data attributes, which is
         safe: those are settings, not cart state, so a module rendered
         before the last cart write still describes the tiers correctly. */
      const tiers = getTierConfig(module);
      const selectedTier = tiers.find(
        (tier) => tier.index === cardTierIndex && tier.threshold === cardThreshold
      );
      const includePrevious = includesPreviousTiers(module);

      try {
        /* Cart state is read from the cart. Never from the rendered module.

           This is the rule the whole file is built on and it was briefly
           broken: eligibility was taken from data-paid-subtotal and the old
           gift's key from data-selected-key. Both are written by Liquid at
           render time, and the module on screen is routinely a step behind
           the cart, most of all in the instant a threshold is crossed,
           which is precisely when this code runs. Automatic selection then
           judged a cart of €50 by the €37 in the markup, found no unlocked
           tier, and declined to add anything.

           The automatic path is handed the cart the reconcile has just
           read, so it still costs no extra request. */
        let cart = options.cart || await getCart();

        const unlockedTier = getUnlockedTier(tiers, paidSubtotal(cart));
        const isAllowedVariant = Boolean(
          selectedTier &&
          unlockedTier &&
          isTierAllowed(selectedTier, unlockedTier, includePrevious) &&
          selectedTier.variantIds.has(variantId)
        );

        if (!isAllowedVariant) {
          if (!isAuto) {
            notify(module, 'Your cart total has changed. Please select a gift from an available tier.');
            /* Put the server's version of the cards back, since the
               optimistic paint promised something that will not happen. */
            refreshThemeCart();
          }
          scheduleReconcile();
          return;
        }

        const previousGiftKeys = giftItems(cart).map((item) => item.key);

        /* Two requests for a swap, one for a first choice. It was six:
           A swap is now four requests and a first choice is two, down from
           six. What went are the duplicate reads: a second cart read inside
           the removal, and a third that asked for a cart already in hand.
           What stays are the two reads that decide something, before and
           after the write. The waitForTheme pauses of up to 700ms each also
           went from this path: they keep our writes clear of the theme's
           position-addressed ones, and every write here is addressed by
           line key, so the hazard does not apply to a tap someone is
           waiting on. */
        if (previousGiftKeys.length) {
          /* Every gift line the cart actually holds, cleared in one request,
             addressed by key. Checked rather than fired and forgotten: if an
             old line survived and the new one were added anyway, the cart
             would hold two gifts, and the duplicate cleanup keeps whichever
             it finds first, which can be the one just replaced. */
          const updates = {};
          previousGiftKeys.forEach((key) => { updates[key] = 0; });

          const removal = await withBusy(() => fetch(`${rootUrl()}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates })
          }));

          if (!removal.ok) throw new Error('Unable to change the gift on your order.');
        }

        if (isAuto) await waitForTheme();
        const response = await withBusy(() => fetch(`${rootUrl()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1,
              properties: {
                _ph_unlock_gift: 'true',
                _ph_unlock_tier: String(selectedTier.index),
                _ph_unlock_threshold: String(selectedTier.threshold),
                /* Records whether the theme chose this gift or the customer
                   did. A customer's own choice is never replaced when a
                   higher tier opens up. */
                _ph_unlock_auto: isAuto ? 'true' : 'false'
              }
            }]
          })
        }));

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'Unable to add this gift.');
        }

        /* cart/add.js returns the JSON of the LINE ITEMS that were added,
           not the cart. Shopify's Ajax reference is explicit about it. v17
           treated the response as a cart, so cart.items was undefined, no
           gift line could be found in it, and every single add ended in
           "The gift could not be discounted to FREE and was removed from
           your cart" while the gift sat correctly in the cart all along.

           This read is not optional. */
        cart = await getCart();

        const result = await validateCart({ cart, module });
        const addedGift = result.validGift;

        if (!addedGift || Number(addedGift.variant_id) !== variantId) {
          throw new Error(
            module.dataset.errorMessage ||
            'The gift could not be validated as FREE and was removed from your cart.'
          );
        }

        /* Whether the drawer has to be rebuilt at all.

           A swap is a zero-priced line out and a zero-priced line in. The
           paid subtotal does not move, so the tier, the reward bar and the
           delivery goal are unchanged; the gift line is not shown in the
           product list; the item count excludes it; the subtotal is the
           same number. The only thing that differs is which card is ticked,
           and that is already on screen.

           So the full section fetch, the repaint of every cart line and the
           rebuild of the recommendations row are all skipped, and with them
           the flash of the row emptying. A tier change, or a gift that did
           not come through at zero, still gets the full refresh. */
        const tierUnchanged =
          Number(result.unlockedTier?.index || 0) === Number(unlockedTier.index);
        const visuallyInert =
          tierUnchanged &&
          !result.changed &&
          Number(addedGift.final_line_price || 0) === 0;

        if (visuallyInert) {
          /* Keep the module's own record of the cart in step, since the
             server-rendered attributes are what the next decision reads. */
          module.dataset.selectedKey = addedGift.key;
          module.dataset.selectedTier = String(cardTierIndex);
          module.dataset.selectedAuto = isAuto ? 'true' : 'false';
          module.dataset.selectedFinalPrice = String(addedGift.final_line_price || 0);
          paintSelection(module, variantId);
        } else {
          refreshThemeCart();
        }
      } catch (error) {
        /* An automatic selection is a convenience, not something the
           customer asked for. Failing quietly is better than an error
           they cannot act on; the manual chooser is still right there. */
        if (isAuto) {
          blockAutoSelect(variantId);
          /* Loud in the console even though it is silent on screen. An
             automatic add that fails is the difference between a customer
             getting their gift and not, so it must never be invisible to
             whoever is looking into it. */
          console.error(
            'Prohealth automatic gift selection failed',
            { variantId, tier: cardTierIndex, threshold: cardThreshold, reason: error.message }
          );
        } else {
          notify(module, error.message);
        }
        /* The optimistic paint was wrong, so the server's version of the
           cart replaces it. */
        refreshThemeCart();
      } finally {
        clearPending(module);
      }
    });

  const selectGift = (card, options = {}) => {
    const module = card.closest(MODULE);
    if (!module) return Promise.resolve();

    /* A pending background reconcile must not get in front of the customer.
       Every write goes through one queue, so a reconcile that was about to
       start would make the tap wait for it, including any cooldown it
       chooses to observe. The reconcile is rescheduled by applyGift's own
       cart event anyway. */
    window.clearTimeout(reconcileTimer);

    /* The tick moves here, in the click handler, synchronously.

       It used to be the first statement inside applyGift, which reads as
       "before any request" but is not: applyGift's body is handed to
       queueOperation, and that runs it only after whatever is already in
       the queue has settled. So with a reconcile in flight the customer's
       tap was painted at the end of that reconcile, not at the tap. Out
       here nothing can defer it, and the server's version replaces it if
       the write is refused. */
    if (options.auto !== true) paintSelection(module, Number(card.dataset.variantId));

    return applyGift(
      {
        card,
        module,
        variantId: Number(card.dataset.variantId),
        tier: Number(card.dataset.tier),
        threshold: Number(card.dataset.threshold)
      },
      options
    );
  };

  /* ---------------------------------------------------------------------
     Automatic gift selection

     One free gift per order, so as soon as a tier is unlocked there is a
     correct default: the first available gift of the highest unlocked
     tier. It is always chosen for the customer, not offered as an option
     to a merchant, because a cart that reaches the threshold and then
     asks for one more decision loses orders at the last step. The cards
     stay on screen, so swapping is still one tap.

     Availability is read from the rendered cards rather than the tier
     data attributes, so a sold-out gift is skipped automatically.
     --------------------------------------------------------------------- */

  /* A variant that failed an automatic add is skipped, so one sold-out gift
     does not stop the rest. The block is deliberately short-lived: it used
     to last the whole page, which meant a single network hiccup left the
     customer with no gift and no way of knowing, for as long as they stayed
     on the page. */
  const AUTO_BLOCK_TTL = 30000;
  const autoSelectBlocked = new Map();
  let autoSelectBusy = false;

  const blockAutoSelect = (variantId) =>
    autoSelectBlocked.set(String(variantId), Date.now() + AUTO_BLOCK_TTL);

  const autoSelectAllowed = (variantId) => {
    const until = autoSelectBlocked.get(String(variantId));
    if (!until) return true;
    if (Date.now() >= until) {
      autoSelectBlocked.delete(String(variantId));
      return true;
    }
    return false;
  };

  const allowed = (variantId) => autoSelectAllowed(variantId);

  /* Which gift to add on the customer's behalf: the first available gift of
     the tier they have just reached.

     Rendered cards are consulted first, because a card carries the one
     thing the tier attributes do not, whether the variant is in stock, so a
     sold-out gift is skipped. When the module on screen has no cards yet,
     which is exactly the case at the instant a threshold is crossed, the
     choice falls back to the tier attributes. If that variant turns out to
     be sold out the add fails, the variant is added to autoSelectBlocked,
     and the next pass moves on to the following one. */
  const defaultGiftChoice = (module, tiers, tierIndex) => {
    const cards = Array.from(module.querySelectorAll(CARD)).filter(
      (card) => !cardUnavailable(card) && allowed(card.dataset.variantId)
    );

    if (cards.length) {
      const preferred = cards.filter((card) => Number(card.dataset.tier) === tierIndex);
      const card = (preferred.length ? preferred : cards)[0];
      return {
        card,
        module,
        variantId: Number(card.dataset.variantId),
        tier: Number(card.dataset.tier),
        threshold: Number(card.dataset.threshold)
      };
    }

    const tier = tiers.find((entry) => entry.index === tierIndex);
    if (!tier) return null;

    const variantId = Array.from(tier.variantIds).find(allowed);
    if (!variantId) return null;

    return { card: null, module, variantId, tier: tier.index, threshold: tier.threshold };
  };

  const maybeAutoSelect = async (module, result) => {
    if (!module || autoSelectBusy) return;
    if (!result || !result.unlockedTier) return;

    const currentGift = result.validGift;

    /* Nothing selected: choose the default. */
    let reason = currentGift ? null : 'empty';

    /* Selected automatically and a higher tier has since opened up:
       move the default up. A gift the customer chose is left alone.
       The tier is read from the gift line rather than from the module,
       whose data-selected-auto belongs to the render on screen and can be
       a step behind the cart. */
    if (!reason && currentGift?.properties?._ph_unlock_auto === 'true') {
      const giftTier = Number(currentGift.properties?._ph_unlock_tier || 0);
      if (result.unlockedTier.index > giftTier) reason = 'upgrade';
    }

    if (!reason) return;

    const choice = defaultGiftChoice(
      module,
      getTierConfig(module),
      result.unlockedTier.index
    );
    if (!choice) return;
    if (currentGift && Number(choice.variantId) === Number(currentGift.variant_id)) return;

    autoSelectBusy = true;
    try {
      /* Painted here, not left until the write returns.

         The tier is known to be unlocked, the variant is known to be one of
         its gifts, and the theme's own re-render has already landed, so
         there is nothing left to wait for before showing the customer the
         gift they have just earned. If the write is refused the catch in
         applyGift repaints from the server.

         result.cart came from the validateCart that just ran, so it is the
         live cart and applyGift needs no read of its own. */
      paintSelection(choice.module, choice.variantId);
      await applyGift(choice, { auto: true, cart: result.cart });
    } finally {
      /* Short cooldown so a failed attempt cannot spin against the
         re-render it triggers. */
      window.setTimeout(() => {
        autoSelectBusy = false;
      }, 600);
    }
  };

  /* ---------------------------------------------------------------------
     Where the row is anchored

     Always on the gift of the highest unlocked tier, whatever is selected.

     Cards are laid out in ascending tier order, so the tier just reached is
     the rightmost and the earlier ones sit to its left, which is the order
     the customer earned them in. Anchoring on the newest one means the gift
     they have just unlocked is the one in front of them, and the ones they
     could swap back to are one swipe left.

     It used to anchor on the selected card, which is the same card whenever
     selection is automatic, but wrong in the one case that matters: a
     customer who chose an earlier gift for themselves would reach a new
     tier and be shown their own existing choice, with the thing they had
     just unlocked off screen.

     Scrolled, never reordered: moving a card changes the list the customer
     is reading, scrolling only changes where they are looking. Instant, not
     smooth, because this runs on render and an animation on load reads as
     the page moving by itself.
     --------------------------------------------------------------------- */
  const anchorTier = (module) => {
    const tierIndex = Number(module.dataset.unlockedTier || 0);
    return tierIndex > 0 ? tierIndex : 0;
  };

  const revealSelectedCard = (module) => {
    /* Nothing to scroll to while the row is closed, and the measurements
       would be zeroes. The anchor runs again when it is opened. */
    if (collapsed(module)) return;

    const carousel = module.querySelector(CAROUSEL);
    const tierIndex = anchorTier(module);
    const card =
      (tierIndex && carousel?.querySelector(`${CARD}[data-tier="${tierIndex}"]`)) ||
      carousel?.querySelector(`${CARD}.is-selected`);
    if (!carousel || !card) return;

    /* Measured from the rendered boxes rather than from offsetLeft.

       offsetLeft is resolved against the nearest positioned ancestor, which
       is not the carousel, so the arithmetic depended on the card and the
       carousel happening to share one. That held until the card became a
       positioned div, and it is a fragile thing to depend on either way.
       Rect deltas plus the current scroll are exact whatever the containing
       blocks are. */
    const carouselBox = carousel.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();
    const cardLeft = carousel.scrollLeft + (cardBox.left - carouselBox.left);
    const cardRight = cardLeft + cardBox.width;
    const viewLeft = carousel.scrollLeft;
    const viewRight = viewLeft + carousel.clientWidth;

    /* Already fully in view: leave the row exactly where it is. */
    if (cardLeft >= viewLeft - 1 && cardRight <= viewRight + 1) return;

    const previousBehaviour = carousel.style.scrollBehavior;
    carousel.style.scrollBehavior = 'auto';
    carousel.scrollLeft = Math.max(
      0,
      Math.min(cardLeft, carousel.scrollWidth - carousel.clientWidth)
    );
    carousel.style.scrollBehavior = previousBehaviour;
  };

  /* ---------------------------------------------------------------------
     Collapsible gift row

     Optional, from the Cart settings, and rendered closed by Liquid so
     there is never a moment of open content before a script closes it.

     The open state is a plain variable in this file, not sessionStorage,
     and that is the whole design. It resets on every page load, so the
     block always starts closed as asked. It survives the drawer being
     re-rendered, which happens on every cart write, so a customer who has
     opened the row does not have it slam shut underneath them the moment
     they choose a gift. Persisting it would have kept it open into the next
     page load, which is not what "always starts collapsed" means.
     --------------------------------------------------------------------- */

  const SELECTION = '.ph-unlock-gifts__selection';
  let rowOpen = false;

  const selectionOf = (element) => element?.closest?.(SELECTION) || null;

  const collapsed = (module) => {
    const selection = module?.querySelector?.(SELECTION);
    return Boolean(selection && selection.classList.contains('is-collapsed'));
  };

  const applyRowState = (module) => {
    const selection = module?.querySelector?.(SELECTION);
    if (!selection || !selection.classList.contains('is-collapsible')) return;

    selection.classList.toggle('is-collapsed', !rowOpen);

    const toggle = selection.querySelector('[data-ph-gift-collapse]');
    if (toggle) toggle.setAttribute('aria-expanded', rowOpen ? 'true' : 'false');
  };

  const toggleRow = (button) => {
    const selection = selectionOf(button);
    const module = button.closest(MODULE);
    if (!selection || !module) return;

    rowOpen = !rowOpen;

    /* Every rendered copy of the block moves together: the cart page and
       the drawer are two views of one cart, and leaving them disagreeing
       about whether the gifts are showing would be its own bug. */
    document.querySelectorAll(MODULE).forEach(applyRowState);

    if (!rowOpen) return;

    /* Opening reveals a row that has never been measured, so the arrows and
       the scroll indicator are decided now, and the anchor puts the tier's
       gift in front of the customer as it does on render. */
    window.requestAnimationFrame(() => {
      revealSelectedCard(module);
      updateScrollReference(module);
    });
  };

  const updateScrollReference = (module) => {
    const carousel = module.querySelector(CAROUSEL);
    const thumb = module.querySelector('[data-ph-gift-scroll-thumb]');
    const track = module.querySelector('[data-ph-gift-scroll-track]');
    const prev = module.querySelector('[data-ph-gift-prev]');
    const next = module.querySelector('[data-ph-gift-next]');
    if (!carousel) return;

    /* A collapsed row is display: none, so every width it reports is zero
       and any decision taken from those numbers would be wrong. The CSS
       already hides both arrows in that state; leave their measured state
       alone until the row is open and can be measured. */
    if (collapsed(module)) return;

    const maxScroll = Math.max(carousel.scrollWidth - carousel.clientWidth, 0);
    const hasOverflow = maxScroll > 4;

    if (prev) prev.hidden = !hasOverflow;
    if (next) next.hidden = !hasOverflow;
    if (track) track.hidden = !hasOverflow;

    /* Shown but inert at each end, rather than removed, so the control row
       keeps its width while the customer scrolls through it. */
    if (prev) prev.dataset.phAtEnd = carousel.scrollLeft <= 4 ? 'true' : 'false';
    if (next) next.dataset.phAtEnd = carousel.scrollLeft >= maxScroll - 4 ? 'true' : 'false';

    if (!thumb || !hasOverflow) return;

    const visibleRatio = Math.min(carousel.clientWidth / carousel.scrollWidth, 1);
    const thumbWidth = Math.max(visibleRatio * 100, 18);
    const travel = 100 - thumbWidth;
    const progress = carousel.scrollLeft / maxScroll;

    thumb.style.width = `${thumbWidth}%`;
    thumb.style.transform = `translateX(${progress * travel / (thumbWidth / 100)}%)`;
  };

  /* ---------------------------------------------------------------------
     The New tag

     A short-lived marker on the gifts of a tier the customer has only just
     unlocked. It exists because their own choice is never replaced, so on
     reaching a new tier the thing that changed is a card, not the
     selection, and a card that appears with no comment is easy to miss.

     Applied by JS rather than by Liquid because it is a moment, not a
     state: Liquid can say which tier is unlocked but not whether the
     customer has already been shown it. SEEN_KEY carries the highest tier
     already announced, per session, so the tag appears exactly once per
     tier and does not come back on every later re-render.
     --------------------------------------------------------------------- */

  const SEEN_KEY = 'phGiftSeenTier';
  const FROM_KEY = 'phGiftNewFrom';
  const UNTIL_KEY = 'phGiftNewUntil';
  const NEW_TAG_MS = 6000;
  const NEW_TAG_CLASS = 'ph-unlock-gift-card__new-badge';

  /* Tier 1 is never new. It is the first gift the customer earns, and there
     is nothing before it to distinguish it from, so the tag starts at tier
     2. Everything unlocked above the last tier they were shown is new,
     which for tier 3 means both of its gifts, not one. */
  const FIRST_TAGGABLE_TIER = 2;

  const readNumber = (key) => {
    try {
      return Number(window.sessionStorage.getItem(key) || 0) || 0;
    } catch (error) {
      /* Private windows and blocked storage throw on access. The tag then
         behaves as though nothing had been announced yet, which is
         harmless. */
      return 0;
    }
  };

  const writeNumber = (key, value) => {
    try {
      window.sessionStorage.setItem(key, String(value));
    } catch (error) {
      /* Nothing to do. */
    }
  };

  const clearNewTags = (module) => {
    module.querySelectorAll(`.${NEW_TAG_CLASS}`).forEach((tag) => tag.remove());
    module.querySelectorAll(`${CARD}.ph-unlock-gift-card--has-new`).forEach((card) => {
      card.classList.remove('ph-unlock-gift-card--has-new');
    });
  };

  const paintNewTags = (module, fromTier, label) => {
    const unlocked = Number(module.dataset.unlockedTier || 0);

    module.querySelectorAll(CARD).forEach((card) => {
      const tier = Number(card.dataset.tier || 0);
      if (tier <= fromTier || tier > unlocked) return;
      if (card.querySelector(`.${NEW_TAG_CLASS}`)) return;

      const tag = document.createElement('span');
      tag.className = NEW_TAG_CLASS;
      tag.textContent = label;
      card.classList.add('ph-unlock-gift-card--has-new');
      card.prepend(tag);
    });
  };

  /* The tag is a window of time, not a one-off paint.

     It used to be applied once, at the render where the new tier appeared,
     and the announcement was recorded immediately. Any re-render inside the
     next few seconds, which in this cart means any quantity change or gift
     write, then found the tier already announced and produced a card with
     no tag: the marker vanished early and unpredictably.

     The window is stored instead, so every render inside it paints the tag
     again and the customer sees it for its full duration however many times
     the drawer is rebuilt underneath.
     --------------------------------------------------------------------- */
  /* ---------------------------------------------------------------------
     Bringing the gift block into view

     The horizontal anchor scrolls the row to the tier's card, but that is
     useless while the whole block sits below the fold: the customer adds a
     product, earns a gift, and nothing on screen moves because the gift is
     three products further down.

     Both surfaces are handled. In the drawer the scrolling element is an
     ancestor of the block, so that ancestor is scrolled. On the cart page
     nothing between the block and the body scrolls, so the window is moved
     instead. That second case was left out at first, on the reasoning that
     moving the whole page under someone is worse than leaving it; she asked
     for it, and being consistent across the two surfaces is worth more than
     that caution, particularly since it happens once per tier.

     Once per tier, driven by the same "newly unlocked" test as the New tag,
     so it never fights a customer who has scrolled somewhere deliberately.
     --------------------------------------------------------------------- */
  const scrollableAncestor = (element) => {
    let node = element?.parentElement;

    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const scrolls = /(auto|scroll)/.test(`${style.overflowY}`);
      if (scrolls && node.scrollHeight > node.clientHeight + 4) return node;
      node = node.parentElement;
    }

    return null;
  };

  const anchorGiftBlock = (module) => {
    const tierIndex = Number(module.dataset.unlockedTier || 0);

    /* A closed row is display: none, so its cards measure as zero and
       scrolling to one would land nowhere. Bring the block itself into
       view instead: the heading and the chevron are what the customer can
       act on while it is closed. */
    const target = collapsed(module)
      ? module
      : module.querySelector(`${CARD}[data-tier="${tierIndex}"]`) ||
        module.querySelector(CARD) ||
        module;

    /* A little above the card, so the heading that explains it comes with
       it rather than the card arriving flush against the top edge. */
    const HEADROOM = 72;
    const scroller = scrollableAncestor(module);

    if (scroller) {
      const offset =
        target.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop;

      scroller.scrollTo({ top: Math.max(0, offset - HEADROOM), behavior: 'smooth' });
      return;
    }

    /* Inside the drawer with nothing to scroll means the drawer already
       fits on screen, so the block is visible and there is nothing to do.
       Falling through here would scroll the page behind the overlay. */
    if (module.closest('m-cart-drawer')) return;

    /* The cart page: the block's ancestors do not scroll, the document
       does. Centred rather than offset from the top, because the page has a
       sticky header whose height this file has no business knowing, and
       centring cannot land the card underneath it. */
    if (typeof target.scrollIntoView !== 'function') return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const markNewTiers = (module) => {
    const label = module.dataset.newLabel || '';
    const unlocked = Number(module.dataset.unlockedTier || 0);

    if (!label || unlocked < FIRST_TAGGABLE_TIER) {
      /* Lower the mark on the way out, not only on the path below. A cart
         that falls back to tier 1 and climbs again has genuinely unlocked
         tier 2 a second time, and without this the mark stayed at the
         highest tier ever reached and the tag never came back. */
      if (unlocked < readNumber(SEEN_KEY)) writeNumber(SEEN_KEY, unlocked);
      clearNewTags(module);
      return;
    }

    const seen = Math.max(readNumber(SEEN_KEY), FIRST_TAGGABLE_TIER - 1);

    if (unlocked > seen) {
      /* A tier has just opened. Everything above the last one announced is
         new, so the whole span is tagged, not only the topmost tier. */
      writeNumber(FROM_KEY, seen);
      writeNumber(UNTIL_KEY, Date.now() + NEW_TAG_MS);
      writeNumber(SEEN_KEY, unlocked);

      /* After the row has been laid out and the horizontal anchor applied,
         so the card is already the one in front of the customer when the
         block arrives. */
      window.setTimeout(() => anchorGiftBlock(module), 260);
    } else if (unlocked < seen) {
      /* Dropped back. Lower the mark so re-earning the tier announces it
         again, which is honest: it has just been unlocked a second time. */
      writeNumber(SEEN_KEY, unlocked);
    }

    const until = readNumber(UNTIL_KEY);
    const remaining = until - Date.now();

    if (remaining <= 0) {
      clearNewTags(module);
      return;
    }

    paintNewTags(module, readNumber(FROM_KEY), label);

    window.setTimeout(() => {
      document.querySelectorAll(MODULE).forEach(clearNewTags);
    }, remaining);
  };

  /* One card at a time, in the given direction, stopping at the ends.

     It used to wrap round to the start on reaching the last card, which
     with a single arrow was the only way back. Now that there is a Back
     arrow, wrapping is a surprise rather than a convenience. */
  const scrollCarousel = (button, direction) => {
    const module = button.closest(MODULE);
    const carousel = module?.querySelector(CAROUSEL);
    const card = carousel?.querySelector(CARD);
    if (!carousel || !card) return;

    const step = (card.getBoundingClientRect().width + 10) * direction;
    const maxScroll = Math.max(carousel.scrollWidth - carousel.clientWidth, 0);

    carousel.scrollTo({
      left: Math.max(0, Math.min(carousel.scrollLeft + step, maxScroll)),
      behavior: 'smooth'
    });
  };

  const bindScrollReferenceDrag = (module) => {
    const carousel = module.querySelector(CAROUSEL);
    const track = module.querySelector('[data-ph-gift-scroll-track]');
    const thumb = module.querySelector('[data-ph-gift-scroll-thumb]');
    if (!carousel || !track || !thumb || track.dataset.phGiftDragBound === 'true') return;

    track.dataset.phGiftDragBound = 'true';

    const scrollFromPointer = (clientX) => {
      const rect = track.getBoundingClientRect();
      const thumbWidth = thumb.getBoundingClientRect().width;
      const available = Math.max(rect.width - thumbWidth, 1);
      const thumbLeft = Math.min(Math.max(clientX - rect.left - (thumbWidth / 2), 0), available);
      const maxScroll = Math.max(carousel.scrollWidth - carousel.clientWidth, 0);
      carousel.scrollLeft = (thumbLeft / available) * maxScroll;
    };

    const stopDragging = (event) => {
      if (track.dataset.phGiftDragging !== 'true') return;
      track.dataset.phGiftDragging = 'false';
      track.classList.remove('is-dragging');
      if (event?.pointerId !== undefined && track.hasPointerCapture?.(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }
    };

    track.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      event.preventDefault();
      track.dataset.phGiftDragging = 'true';
      track.classList.add('is-dragging');
      track.setPointerCapture?.(event.pointerId);
      scrollFromPointer(event.clientX);
    });

    track.addEventListener('pointermove', (event) => {
      if (track.dataset.phGiftDragging === 'true') scrollFromPointer(event.clientX);
    });
    track.addEventListener('pointerup', stopDragging);
    track.addEventListener('pointercancel', stopDragging);
  };

  /* ---------------------------------------------------------------------
     Choosing a gift is bound once, on the document

     It used to be a listener added to each module by bindModule, and that
     had three ways of quietly ceasing to exist, all of which look to a
     customer like a card that does not respond:

       1. bindModule set module.dataset.phGiftBound = 'true' as its FIRST
          statement and added the click listener as its LAST. Everything
          between the two is decoration: the drag handle, a ResizeObserver,
          measurements of a row whose images are still loading. One throw in
          any of it and the click listener was never added, while the flag
          said the module was bound, so nothing ever retried.
       2. The module is replaced wholesale every time the cart re-renders,
          so the listener had to be re-added, correctly, every time.
       3. On the cart page there are two modules, and each needed its own.

     One delegated listener on the document has none of those failure
     modes. It survives every re-render, cannot be lost to an unrelated
     error, and covers both surfaces.
     --------------------------------------------------------------------- */
  const bindGiftCardClicks = () => {
    if (document.documentElement.dataset.phGiftClickBound === 'true') return;
    document.documentElement.dataset.phGiftClickBound = 'true';

    /* The title is a link out to the product page. It sits inside the card,
       which is itself the selection surface, so it has to be allowed through
       untouched: a customer reading what is in a gift is not choosing it. */
    const isGiftLink = (target) => Boolean(target.closest?.('[data-ph-gift-link]'));

    document.addEventListener('click', (event) => {
      if (isGiftLink(event.target)) return;

      const card = event.target.closest?.(CARD);
      if (card) {
        if (!card.closest(MODULE)) return;
        if (cardUnavailable(card)) return;
        event.preventDefault();
        selectGift(card).catch(() => undefined);
        return;
      }

      const collapse = event.target.closest?.('[data-ph-gift-collapse]');
      if (collapse && collapse.closest(MODULE)) {
        event.preventDefault();
        toggleRow(collapse);
        return;
      }

      const prev = event.target.closest?.('[data-ph-gift-prev]');
      if (prev && prev.closest(MODULE)) {
        scrollCarousel(prev, -1);
        return;
      }

      const next = event.target.closest?.('[data-ph-gift-next]');
      if (next && next.closest(MODULE)) scrollCarousel(next, 1);
    });

    /* Enter and Space, which a native button would have given for free. */
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      if (isGiftLink(event.target)) return;

      const card = event.target.closest?.(CARD);
      if (!card || !card.closest(MODULE) || cardUnavailable(card)) return;

      event.preventDefault();
      selectGift(card).catch(() => undefined);
    });
  };

  /* Everything else about the module is presentation, and none of it may
     be allowed to take the click handler down with it, so it is bound
     separately and wrapped. */
  const bindModule = (module) => {
    if (module.dataset.phGiftBound === 'true') return;
    module.dataset.phGiftBound = 'true';

    /* Outside the try: if the customer has opened the row, a re-render must
       not leave it shut because a piece of decoration below threw. */
    applyRowState(module);

    try {
      const carousel = module.querySelector(CAROUSEL);
      bindScrollReferenceDrag(module);

      markNewTiers(module);
      predictSelection(module);

      if (carousel) {
        carousel.addEventListener('scroll', () => updateScrollReference(module), { passive: true });
        window.requestAnimationFrame(() => {
          revealSelectedCard(module);
          updateScrollReference(module);
        });

        /* Card images are lazy, so the first measurement can be taken
           before the row has its real width. One late pass settles it. */
        window.setTimeout(() => revealSelectedCard(module), 400);

        if ('ResizeObserver' in window) {
          const resizeObserver = new ResizeObserver(() => updateScrollReference(module));
          resizeObserver.observe(carousel);
        } else {
          window.addEventListener('resize', () => updateScrollReference(module), { passive: true });
        }
      }
    } catch (error) {
      console.error('Prohealth gift carousel setup:', error);
    }
  };

  const continueCheckout = (control) => {
    checkoutBypass = true;

    if (control instanceof HTMLAnchorElement) {
      window.location.assign(control.href);
      return;
    }

    control.disabled = false;
    control.click();
    window.setTimeout(() => {
      checkoutBypass = false;
    }, 0);
  };

  const guardCheckout = async (control) => {
    const context = control.closest?.('m-cart, m-cart-drawer, form') || document;
    const module = getReferenceModule(context);
    if (!module) {
      continueCheckout(control);
      return;
    }

    const canDisable = 'disabled' in control;
    const wasDisabled = canDisable ? control.disabled : false;
    if (canDisable) control.disabled = true;
    control.setAttribute('aria-busy', 'true');

    try {
      /* Nothing here blocks checkout any more. A gift is always selected
         for the customer, so there is no missing choice to stop them for.
         The reconcile still runs, because it is what guarantees the gift
         line is genuinely at zero before the order is placed. */
      await reconcileNow({ module, notifyOnError: true });
      continueCheckout(control);
    } catch (error) {
      notify(module, 'Your cart could not be verified. Please try again.', 'error');
    } finally {
      control.removeAttribute('aria-busy');
      if (!checkoutBypass && canDisable) control.disabled = wasDisabled;
    }
  };

  const bindCheckoutGuard = () => {
    if (document.documentElement.dataset.phGiftCheckoutBound === 'true') return;
    document.documentElement.dataset.phGiftCheckoutBound = 'true';

    document.addEventListener('click', (event) => {
      const control = event.target.closest(
        'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]'
      );
      if (!control || checkoutBypass || control.disabled) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      guardCheckout(control);
    }, true);

    document.addEventListener('submit', (event) => {
      if (checkoutBypass) return;
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout' || submitter.disabled) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      guardCheckout(submitter);
    }, true);
  };

  const init = () => {
    /* Order matters. The two things a customer can act on are bound first,
       and each is independent, so a failure in one cannot take the others
       with it. */
    bindGiftCardClicks();
    bindCheckoutGuard();
    document.querySelectorAll(MODULE).forEach(bindModule);
    patchCartCount();
    patchDrawerRender();
    scheduleReconcile();
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);

  /* The cart event subscription is what makes every gift rule run at all,
     so it is not left to whether the event bus happened to exist the
     instant this file was parsed. MinimogEvents comes from a bundled
     module in vendor.min.js; this file is a separate deferred script and
     has no guarantee of order against it. Poll briefly, subscribe once. */
  const subscribeToCartUpdates = () => {
    let attempts = 0;

    const attach = () => {
      const events = window.MinimogEvents;
      const names = window.MinimogTheme?.pubSubEvents;

      if (events && names?.cartUpdate) {
        events.subscribe(names.cartUpdate, (event) => {
          /* A write by the theme or by another of our modules. Start the
             cooldown so nothing here writes on top of it, unless the event
             is our own, which would postpone us behind ourselves. */
          if (event?.source !== 'prohealth-unlocked-gift') lastThemeMutation = Date.now();
          window.setTimeout(init, 80);
        });

        /* openCartDrawer is what the theme emits on add to cart, and it is
           emitted by other add paths too. patchDrawerRender turns the main
           one into a proper cartUpdate; this covers anything that opens the
           drawer without going through renderContents, and costs one cart
           read. */
        if (names.openCartDrawer) {
          events.subscribe(names.openCartDrawer, () => {
            lastThemeMutation = Date.now();
            window.setTimeout(init, 120);
          });
        }

        return;
      }

      attempts += 1;
      if (attempts <= 40) window.setTimeout(attach, 50);
    };

    attach();
  };

  subscribeToCartUpdates();

  /* Last line of defence. If a cart surface is repainted by a route that
     announces nothing at all, the module node itself is still replaced, and
     that is observable. Re-bind, and reconcile as well, because a fresh
     module with no gift in it is precisely the state that needs acting on.
     scheduleReconcile is debounced, and a reconcile that changes nothing
     writes nothing, so this cannot spin. */
  const observer = new MutationObserver((records) => {
    let moduleAdded = false;

    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1 || moduleAdded) return;
        if (node.matches?.(MODULE) || node.querySelector?.(MODULE)) moduleAdded = true;
      });
    });

    document.querySelectorAll(MODULE).forEach(bindModule);
    if (moduleAdded) scheduleReconcile();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  /* One command that answers "why is there no gift", in the browser
     console, without a rebuild:

       ProhealthCartUnlockedGifts.debug()

     It reports what the module was configured with, what the cart actually
     contains, which tier that unlocks, whether a gift line is present and
     valid, and which card the automatic chooser would pick. Read-only:
     it changes nothing. */
  const debug = async () => {
    const module = getReferenceModule();
    const cart = await getCart();

    if (!module) {
      const report = { module: null, note: 'No [data-ph-unlock-gifts] in the DOM.', cart };
      console.table?.([{ module: 'missing' }]);
      return report;
    }

    const tiers = getTierConfig(module);
    const subtotal = paidSubtotal(cart);
    const unlockedTier = getUnlockedTier(tiers, subtotal);
    const includePrevious = includesPreviousTiers(module);
    const gifts = giftItems(cart);
    const choice = unlockedTier ? defaultGiftChoice(module, tiers, unlockedTier.index) : null;

    const report = {
      clickBound: document.documentElement.dataset.phGiftClickBound === 'true',
      subtotal,
      tiers: tiers.map((tier) => ({
        index: tier.index,
        threshold: tier.threshold,
        variants: Array.from(tier.variantIds)
      })),
      unlockedTier: unlockedTier ? unlockedTier.index : null,
      includePrevious,
      cardsInDom: module.querySelectorAll(CARD).length,
      cardsEnabled: Array.from(module.querySelectorAll(CARD)).filter((c) => !c.disabled).length,
      giftLines: gifts.map((item) => ({
        key: item.key,
        variant: item.variant_id,
        quantity: item.quantity,
        linePrice: item.final_line_price,
        tier: item.properties?._ph_unlock_tier,
        auto: item.properties?._ph_unlock_auto,
        valid: giftMatchesAllowedTier(item, tiers, unlockedTier, includePrevious)
      })),
      autoWouldPick: choice
        ? { variantId: choice.variantId, tier: choice.tier, fromCard: Boolean(choice.card) }
        : null,
      blockedVariants: Array.from(autoSelectBlocked.keys()),
      busyCount,
      autoSelectBusy
    };

    console.log('Prohealth unlocked gifts', report);
    return report;
  };

  window.ProhealthCartUnlockedGifts = {
    init,
    debug,
    reconcile: scheduleReconcile,
    reconcileNow,
    afterCartMutation: (cart) => reconcileNow({ cart, refresh: true, notifyOnError: true }),
    getPaidSubtotal: paidSubtotal
  };
})();
